import {BaseApi} from './BaseApi'

// Document Interfaces
export interface DocumentFolder {
  name: string
  files: DocumentFile[]
  isSystemFolder?: boolean // True if folder is system-created and cannot be renamed/deleted
}

export interface DocumentFile {
  _id: string
  fileName: string
  fileSize: number
  uploadDate: Date
  isPrivate: boolean
  uploadedBy: string
}

export interface DocumentResponse {
  _id: string
  entityId: string
  entityType: string
  uploadedBy: string
  uploadedAt: Date
  folders: DocumentFolder[]
}

export interface CreateFolderRequest {
  documentId: string
  name: string
}

export interface RenameFolderRequest {
  documentId: string
  currentFolderName: string
  newFolderName: string
}

export interface UploadFileRequest {
  documentId: string
  folderName: string
  isPrivate: boolean
  file: File
}

export interface DeleteFileRequest {
  documentId: string
  folderName: string
  fileId: string
}

export interface DownloadFileRequest {
  documentId: string
  folderName: string
  fileId: string
}

/**
 * Document API Service
 * Handles all document-related operations (folders, files, uploads, downloads)
 */
export class DocumentApi extends BaseApi {
  /**
   * Get document by entity ID
   */
  async getDocumentByEntityId(entityId: string, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.get<DocumentResponse>(
        `/document/getDocumentByEntityId/${entityId}`,
        {params},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching document by entity ID:', entityId, error)
      throw error
    }
  }

  /**
   * Create a new folder in a document
   */
  async createFolder(request: CreateFolderRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.put<DocumentResponse>('/document/createFolder', request, {
        params,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error creating folder:', request.name, error)
      throw error
    }
  }

  /**
   * Rename an existing folder
   */
  async renameFolder(request: RenameFolderRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.put<DocumentResponse>('/document/renameFolder', request, {
        params,
      })
      return response.data
    } catch (error: unknown) {
      console.error(
        'Error renaming folder:',
        request.currentFolderName,
        '->',
        request.newFolderName,
        error,
      )
      throw error
    }
  }

  /**
   * Upload a file to a folder
   * Uses FormData for multipart/form-data uploads
   */
  async uploadFile(request: UploadFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const formData = new FormData()
      formData.append('file', request.file)

      const params = agencyId
        ? {
            agencyId,
            documentId: request.documentId,
            folderName: request.folderName,
            isPrivate: request.isPrivate,
          }
        : {
            documentId: request.documentId,
            folderName: request.folderName,
            isPrivate: request.isPrivate,
          }

      const response = await this.put<DocumentResponse>(
        `/document/uploadFile/${request.documentId}/${request.folderName}`,
        formData,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error uploading file:', request.file.name, error)
      throw error
    }
  }

  /**
   * Delete a file from a folder
   */
  async deleteFile(request: DeleteFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.put<DocumentResponse>(
        `/document/deleteFile?documentId=${request.documentId}&folderName=${request.folderName}&fileId=${request.fileId}`,
        undefined,
        {params},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error deleting file:', request.fileId, error)
      throw error
    }
  }

  /**
   * Download a file from a folder
   * Returns a Blob for downloading
   */
  async downloadFile(request: DownloadFileRequest, agencyId?: string): Promise<Blob> {
    try {
      const params = {
        documentId: request.documentId,
        folderName: request.folderName,
        fileId: request.fileId,
        ...(agencyId && {agencyId}),
      }

      const response = await this.get<Blob>('/document/downloadFile', {
        params,
        responseType: 'blob',
      })

      return new Blob([response.data])
    } catch (error: unknown) {
      console.error('Error downloading file:', request.fileId, error)
      throw error
    }
  }
}

// Export singleton instance
export const documentApi = new DocumentApi()
